import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '../TextField/TextField';
import InputWithAutocomplete from '../InputWithAutocomplete/InputWithAutocomplete';
import { minSymbolQTY, validationMessages } from '../../../../utils/constants';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import api from '../../../../api/apiSettings';
import colors from '../../../../scss/colors';

export function AddressInput({ userData, updateUserData, disabled, edit, errors }) {
  const { AddressLine1, AddressLine2, City, State, Country } = userData || {};

  const [country, setCountry] = useState(Country);
  const [state, setState] = useState(State);
  const [city, setCity] = useState(City);
  const [address1, setAddress1] = useState(AddressLine1);
  const [address2, setAddress2] = useState(AddressLine2);
  const [stateDisabeld, setStateDisabled] = useState(true);
  const [cityDisabled, setCityDisabled] = useState(true);

  const [autocompleteCountry, setAutocompleteCountry] = useState([]);
  const [autocompleteState, setAutocompleteState] = useState([]);
  const [autocompleteCities, setAutocompleteCities] = useState([]);

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const clearSuggestions = (entity) => {
    switch (entity) {
      case 'country':
        setAutocompleteCountry([]);
        break;
      case 'state':
        setAutocompleteState([]);
        break;
      case 'city':
        setAutocompleteCities([]);
        break;
      default:
        break;
    }
  };

  const handlersByEntity = {
    country: async (value) => {
      const isValid = value && value.length >= minSymbolQTY.COUNTRY;

      // Reset dependent fields
      setState('');
      setCity('');
      setAutocompleteCities([]);
      setAutocompleteState([]);

      // Update country
      setCountry(value);
      updateUserData((x) => ({ ...x, Country: value }));

      // Enable/disable inputs based on country validity
      setStateDisabled(!isValid);
      setCityDisabled(!isValid);

      if (!isValid) return;

      const result = await api.get(`v1/get-countries?keyword=${value}`);
      setAutocompleteCountry(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },

    state: async (value) => {
      if (!country || country.length < minSymbolQTY.COUNTRY) return;

      const isValid = value && value.length >= minSymbolQTY.STATE;

      // Update state
      setState(value);
      updateUserData((x) => ({ ...x, State: value }));

      if (!isValid) {
        setAutocompleteState([]);
        return;
      }

      const result = !disabled && await api.get(`v1/get-states-by-country?countryName=${country}&keyword=${value}`);
      setAutocompleteState(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },

    city: async (value) => {
      if (!country || country.length < minSymbolQTY.COUNTRY) return;

      const isValid = value && value.length >= minSymbolQTY.CITY;

      // Update city
      setCity(value);
      updateUserData((x) => ({ ...x, City: value }));

      if (!isValid) return;

      const query = `v1/get-cities-by-state?countryName=${country}` +
        (state ? `&stataName=${state}` : '') +
        `&keyword=${value}`;

      const result = await api.get(query);
      setAutocompleteCities(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    }

  };

  const inputHandler = ({ entity, value }) => {
    handlersByEntity[entity](value);
  };

  useEffect(() => {
    setState(State);
    setCountry(Country);
    setCity(City);
  }, [Country, State, City]);

  return (
    <div className="mt-6 w-full">
      <div className="text-sm font-medium text-black mb-4 border-b-2 border-gray-300 pb-2">{t('common.forms.location')}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label={t('common.forms.address1')}
          labelClass="text-sm"
          inputClass="!text-sm p-3 text-black !font-normal !rounded-lg"
          value={address1}
          onChange={(e) => {
            updateUserData((x) => ({ ...x, AddressLine1: e.target.value }));
            setAddress1(e.target.value);
          }}
          disabled={disabled}
        />
        <TextField
          label={t('common.forms.address2')}
          labelClass="text-sm"
          inputClass="!text-sm p-3 text-black !font-normal !rounded-lg"
          value={address2}
          onChange={(e) => {
            updateUserData((x) => ({ ...x, AddressLine2: e.target.value }));
            setAddress2(e.target.value);
          }}
          disabled={disabled}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className='gap-2 flex flex-col'>
          <label className="!flex text-sm">{t('common.forms.country')}</label>
          <InputWithAutocomplete
            entity="country"
            error={errors?.country}
            disabled={disabled}
            autocompleteValues={autocompleteCountry}
            fieldValue={country}
            changeHandler={inputHandler}
            placeholder={`${t('common.forms.country')}${t('common.forms.required')}`}
            clearSuggestions={clearSuggestions}
            disableLegend
          />
          {country?.length !== 0 && country?.length < minSymbolQTY.COUNTRY && (
            <ValidationErrorLabel message={validationMessage.COUNTRY_MIN_CHAR} />
          )}
        </div>
        <div className='gap-2 flex flex-col'>
          <label className="!flex text-sm">{t('common.forms.state')}</label>
          <InputWithAutocomplete
            entity="state"
            error={errors?.state}
            disabled={stateDisabeld}
            autocompleteValues={autocompleteState}
            fieldValue={state}
            changeHandler={inputHandler}
            placeholder={`${t('common.forms.state')}${country !== 'United States' ? t('common.forms.optional') : t('common.forms.required')}`}
            clearSuggestions={clearSuggestions}
            disableLegend
          />
          {state?.length !== 0 && state?.length < minSymbolQTY.STATE && (
            <ValidationErrorLabel message={validationMessage.STATE_MIN_CHAR} />
          )}
        </div>

        <div className='gap-2 flex flex-col'>
          <label className="!flex text-sm">{t('common.forms.city')}</label>
          <InputWithAutocomplete
            entity="city"
            error={errors?.city}
            disabled={cityDisabled}
            autocompleteValues={autocompleteCities}
            fieldValue={city}
            changeHandler={inputHandler}
            disableLegend
            placeholder={`${t('common.forms.city')}${t('common.forms.required')}`}
            clearSuggestions={clearSuggestions}
            sx={{
              padding: '0',
              fieldset: {
                top: 0
              }
            }}
          />
          {city?.length !== 0 && city?.length < minSymbolQTY.CITY && (
            <ValidationErrorLabel message={validationMessage.CITY_MIN_CHAR} />
          )}
        </div>
      </div>
    </div>
  );
}
